import { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Image, Input, Modal, Row } from "antd";
import FileTree from "../pages/FileManager/FileTree";
import FileGrid from "../pages/FileManager/FileGrid";
import useStorage from "../stores/storage";
import { useTranslation } from "react-i18next";

const { Search } = Input;

interface ImageType {
  path?: string;
  src?: number;
}

interface Props {
  value?: ImageType;
  onChange?: (value: ImageType) => void;
}

const FileExplorer = () => {
  const {
    fetchCurrentDirectory,
    fetchFolderTree,
    updateHistory,
    currentRef,
    pathList,
    searchFlag
  } = useStorage();

  const refresh = async () => {
    if (currentRef) {
      await fetchCurrentDirectory();
    }

    if (pathList.length === 0) {
      await fetchFolderTree();
    } else {
      updateHistory(String(currentRef), pathList[0]);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Row wrap={false}>
      {
        !searchFlag && (
          <Col flex="256px" style={{ margin: "0px 8px 0px 16px" }}>
            <FileTree />
          </Col>
        )
      }
      <Col flex="auto" style={{ margin: "0px 16px 0px 8px" }}>
        <FileGrid multiple={false} />
      </Col>
    </Row>
  );
};

const ImageSelect = ({ value = {}, onChange }: Props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const {
    currentRef,
    resetAll,
    selected,
    fileList,
    search,
    fetchCurrentDirectory,
    pathList,
    fetchFolderTree,
    updateHistory,
    setSearchFlag
  } = useStorage();

  useEffect(() => {
    resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [image, setImage] = useState<ImageType>({
    path: "",
    src: 0,
  });

  const triggerChange = (changedValue: ImageType) => {
    onChange?.({ ...value, ...image, ...changedValue });
  };

  const handleSearch = async (val: string) => {
    if (val) { // 有值
      setSearchFlag(true);

      await search(val);
    } else {
      setSearchFlag(false);

      if (currentRef) {
        await fetchCurrentDirectory();
      }
  
      if (pathList.length === 0) {
        await fetchFolderTree();
      } else {
        updateHistory(String(currentRef), pathList[0]);
      }
    }
  }

  const handleSubmit = () => {
    if (selected.length < 1) return;

    const selectedId = selected[0];
    const selectedPath = fileList.find((file) => file.id === selectedId)?.path;

    if (!selectedPath) return;

    if (!("path" in value) || !("src" in value))
      setImage({ path: selectedPath, src: selectedId });

    triggerChange({ path: selectedPath, src: selectedId });
    setVisible(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Button type="default" icon={<PlusOutlined />} onClick={show}>
          {t("model.imageSelect.select")}
        </Button>
      </div>
      <div>
        {(image.path || value.path) !== "" && (
          <Image
            placeholder
            src={image.path || value.path}
            preview={true}
            fallback="data:image/png;base64,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"
          />
        )}
      </div>
      <Modal
        title={
          <div className='modal-title'>
            <span
              style={{
                display: "inlineBlock",
                verticalAlign: 'middle',
                lineHeight: '32px',
                marginRight: '12px',
              }}
            >
              {t("model.imageSelect.title")}
            </span>
            <Search
              placeholder={t("model.imageSelect.search")}
              allowClear
              onSearch={handleSearch}
              style={{ width: 260 }}
            />
          </div>
        }
        visible={visible}
        onOk={handleSubmit}
        onCancel={hide}
        centered
        destroyOnClose
        width={"90%"}
        bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
      >
        <FileExplorer key={currentRef} />
      </Modal>
    </div>
  );
};

export default ImageSelect;
